import './SubTrays.scss'
import { Add } from '@mui/icons-material'
import { Button } from '@mui/material'
import { LoadingButton } from 'components/mui'
import useSubTraysLogic from './SubTrays.logic'
import { DataTable } from 'components/molecules/DataTable/DataTable'
import { useAddTrayContext } from '../AddTray.context'
import AnalyzeTray from '../AnalyzeTray/AnalyzeTray'
import TrayContent from '../TrayContent/TrayContent'
import AlertDialog from 'components/AlertDialog/AlertDialog'
import { useIsMobile } from 'lib/utils/mediaQueries'
import { useMediaQuery } from '@mui/material'

const TrayTypeSelection = () => {
  const {
    handleAddInstrumentTrayClick,
    handleAddImplantTrayClick,
    handleAddInstrumentTrayWithImplantSubTraysClick,
    handleBack,
    handleAddCustomImplantTrayClick,
  } = useSubTraysLogic()

  return (
    <div className="tray-type-selection">
      <h2>Select Tray Type</h2>
      <p>Please identify the type of tray you want to add:</p>

      <div className="tray-type-buttons">
        <LoadingButton
          onClick={handleAddInstrumentTrayClick}
          startIcon={<Add />}
          variant="contained"
          fullWidth
        >
          Instrument Only Tray
        </LoadingButton>
        <LoadingButton
          onClick={handleAddImplantTrayClick}
          startIcon={<Add />}
          variant="contained"
          fullWidth
        >
          Implant Only Tray
        </LoadingButton>
        <LoadingButton
          onClick={handleAddInstrumentTrayWithImplantSubTraysClick}
          startIcon={<Add />}
          variant="contained"
          fullWidth
        >
          Instrument Tray With Implant Sub-Tray(s)
        </LoadingButton>
        <LoadingButton
          onClick={handleAddCustomImplantTrayClick}
          startIcon={<Add />}
          variant="contained"
          fullWidth
        >
          Custom Implant Tray
        </LoadingButton>
      </div>

      <div className="buttons_container">
        <Button variant="outlined" onClick={handleBack} fullWidth>
          Back
        </Button>
      </div>
    </div>
  )
}

const SubTraysTable = () => {
  const { subTrays, isFixingRejectedTray } = useAddTrayContext()
  const {
    handleAddInstrumentTrayClick,
    handleAddImplantTrayClick,
    handleAddInstrumentWithSubTraysClick,
    subTraysTableHeaderCells,
    subTraysTableBodyCells,
    handleNext,
    handleBack,
    showDeleteConfirmation,
    handleConfirmDelete,
    handleCancelDelete,
    handleAddCustomImplantTrayClick,
  } = useSubTraysLogic()

  const isMobile = useIsMobile()
  const isSmallTablet = useMediaQuery('(max-width:768px)')

  return (
    <div className="sub-trays-step">
      <AlertDialog
        isOpen={showDeleteConfirmation}
        mode="error"
        title="Delete Sub-Tray"
        description="Are you sure you want to delete this sub-tray? This action cannot be undone."
        primaryButtonText="Delete"
        primaryButtonAction={handleConfirmDelete}
        secondaryButtonText="Cancel"
        secondaryButtonAction={handleCancelDelete}
        zIndex={9999}
      />
      {!isFixingRejectedTray && (
        <div
          className={`sub-trays-actions ${
            isMobile
              ? 'sub-trays-actions--mobile'
              : isSmallTablet
              ? 'sub-trays-actions--tablet'
              : 'sub-trays-actions--desktop'
          }`}
        >
          <LoadingButton
            onClick={handleAddImplantTrayClick}
            startIcon={<Add />}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'small'}
            className="action-button"
          >
            {isMobile ? 'Add Implant Sub Tray' : 'Implant Sub Tray'}
          </LoadingButton>
          <LoadingButton
            onClick={handleAddInstrumentTrayClick}
            startIcon={<Add />}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'small'}
            className="action-button"
          >
            {isMobile
              ? 'Add Instrument Sub-Tray(s)/Level(s)'
              : 'Instrument Sub-Tray(s)'}
          </LoadingButton>
          <LoadingButton
            startIcon={<Add />}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'small'}
            onClick={handleAddCustomImplantTrayClick}
            className="action-button"
          >
            {isMobile ? 'Add Custom Implant Tray' : 'Custom Implant'}
          </LoadingButton>
          <LoadingButton
            onClick={handleAddInstrumentWithSubTraysClick}
            startIcon={<Add />}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'small'}
            className="action-button"
          >
            {isMobile
              ? 'Add Instrument Sub Tray w/ Implants'
              : 'Instrument w/ Implants'}
          </LoadingButton>
        </div>
      )}

      <div className="sub-trays-table_container">
        <DataTable
          tableHeaders={subTraysTableHeaderCells}
          tableRows={subTraysTableBodyCells(subTrays)}
          isLoading={false}
          isErrored={false}
          disableSorting
        />
        <div className="buttons_container">
          <Button variant="outlined" onClick={handleBack} fullWidth>
            Back
          </Button>
          {subTrays.length > 0 ? (
            <LoadingButton variant="contained" onClick={handleNext} fullWidth>
              Next
            </LoadingButton>
          ) : (
            <div />
          )}
        </div>
      </div>
    </div>
  )
}

const SubTrays = () => {
  const { currentView, selectedTrayIndex } = useSubTraysLogic()
  const { hasSubTrays } = useAddTrayContext()

  if (currentView === 'analyze') {
    return <AnalyzeTray />
  }

  if (currentView === 'details' && selectedTrayIndex !== undefined) {
    return <TrayContent trayIndex={selectedTrayIndex} />
  }

  // If there are no sub-trays and we're in the list view, show the tray type selection
  if (!hasSubTrays && currentView === 'list') {
    return <TrayTypeSelection />
  }

  return <SubTraysTable />
}

const WrappedSubTrays = () => {
  return <SubTrays />
}

export default WrappedSubTrays
